// src/components/Seo.js
import { useEffect } from 'react';

// Base MAINNET + Video constants
const BASE_CHAIN_ID = '8453';
const THEME_COLOR = '#0b0614';
const VIDEO_URL = 'https://pub-fe155522e76e45dab08986eda5f3b6e1.r2.dev/media/welcome-casino.mp4';

// --- Site-wide defaults (edit safely here) ---
const SITE_NAME = 'ChainPlay';
const SITE_URL = 'https://chainplay.bet';
const DEFAULT_TITLE = 'ChainPlay – The #1 Blockchain Casino';
const DEFAULT_DESC = 'Play provably‑fair games on Base with instant payouts, on‑chain jackpots, and VIP rakeback — Crash, Dice, Coinflip, Hi‑Lo, Plinko, Mines, Keno & more.';
const DEFAULT_IMAGE = `${SITE_URL}/bluedice1200.png`; // 1200×630 recommended
const DEFAULT_TWITTER = '@chainplaydotbet';
const DEFAULT_KEYWORDS = [
  'chainplay', 'blockchain casino', 'on-chain casino', 'crypto casino', 'provably fair',
  'crash game', 'dice', 'coinflip', 'hilo', 'plinko', 'mines', 'keno', 'web3 gaming', 'base chain casino'
].join(', ');

// Small SSR-safe helpers
const safeDocument = () => { try { return typeof document !== 'undefined' ? document : null; } catch { return null; } };

const safeURL = (u) => { try { return new URL(u); } catch { return null; } };
const hostFrom = (u) => { const x = safeURL(u); return x ? `${x.protocol}//${x.host}` : null; };

function upsertMetaByName(d, name, content) {
  if (!d) return; if (content == null) return;
  let el = d.querySelector(`meta[name="${name}"]`);
  if (!el) { el = d.createElement('meta'); el.setAttribute('name', name); d.head.appendChild(el); }
  el.setAttribute('content', String(content));
}
function upsertMetaByProp(d, prop, content) {
  if (!d) return; if (content == null) return;
  let el = d.querySelector(`meta[property="${prop}"]`);
  if (!el) { el = d.createElement('meta'); el.setAttribute('property', prop); d.head.appendChild(el); }
  el.setAttribute('content', String(content));
}
function upsertLink(d, rel, href, extra = {}) {
  if (!d || !href) return;
  let el = null;
  // Allow multiple icons (match by sizes/type), alternates (match by hreflang), and preconnect/dns-prefetch (match by href)
  if (rel === 'icon') {
    const parts = [`link[rel="icon"]`];
    if (extra.sizes) parts.push(`[sizes="${extra.sizes}"]`);
    if (extra.type) parts.push(`[type="${extra.type}"]`);
    el = d.querySelector(parts.join(''));
  } else if (rel === 'alternate' && (extra.hreflang || extra.hrefLang)) {
    const lang = extra.hreflang || extra.hrefLang; // normalize legacy param
    el = d.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
    // ensure attribute key is normalized later
    extra.hreflang = lang; delete extra.hrefLang;
  } else if (rel === 'preconnect' || rel === 'dns-prefetch') {
    el = d.querySelector(`link[rel="${rel}"][href="${href}"]`);
  } else {
    el = d.querySelector(`link[rel="${rel}"]`);
  }
  if (!el) { el = d.createElement('link'); el.setAttribute('rel', rel); d.head.appendChild(el); }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([k, v]) => { if (v != null) el.setAttribute(k.toLowerCase(), String(v)); });
}
function upsertScriptJsonLd(d, id, data) {
  if (!d || !data) return;
  let el = d.getElementById(id);
  if (!el) { el = d.createElement('script'); el.id = id; el.type = 'application/ld+json'; d.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

// Env + runtime helpers for multichain SEO hints
const getEnv = (k, fallback = '') => { try { return (typeof process !== 'undefined' && process.env && process.env[k]) || fallback; } catch { return fallback; } };
const resolveActiveChainId = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('cp:lastChainId');
      if (saved && !Number.isNaN(Number(saved))) return String(Number(saved));
    }
  } catch {}
  return String(
    Number(
      getEnv('REACT_APP_CHAIN_ID') ||
      getEnv('VITE_CHAIN_ID') ||
      getEnv('REACT_APP_CHAIN_ID_8453') ||
      BASE_CHAIN_ID
    )
  );
};
const resolveExplorer = (chainId) => {
  const id = String(chainId || '');
  const byId =
    getEnv(`REACT_APP_BLOCK_EXPLORER_${id}`) ||
    getEnv(`VITE_BLOCK_EXPLORER_${id}`) ||
    getEnv(`NEXT_PUBLIC_BLOCK_EXPLORER_${id}`, '');
  if (byId) return byId;
  const generic =
    getEnv('REACT_APP_BLOCK_EXPLORER') ||
    getEnv('VITE_BLOCK_EXPLORER') ||
    getEnv('NEXT_PUBLIC_BLOCK_EXPLORER', '');
  if (generic) return generic;
  // Safe defaults for known chains (Base today)
  if (id === '8453') return 'https://basescan.org';
  return '';
};

/**
 * SEO component without external dependencies.
 * Injects meta/link/script tags directly into <head> on mount/update.
 */
export default function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESC,
  path = '/',
  image = DEFAULT_IMAGE,
  robots = 'index,follow',
  noindex = false,
  type = 'website',
  locale = 'en_US',
  siteName = SITE_NAME,
  keywords = DEFAULT_KEYWORDS,
  publishedTime,
  modifiedTime,
  children, // not used here; reserved for compatibility
}) {
  const d = safeDocument();
  const safePath = typeof path === 'string' ? (path.startsWith('/') ? path : `/${path}`) : '/';
  const url = `${SITE_URL}${safePath}`;
  const finalTitle = title || DEFAULT_TITLE;
  const robotsContent = noindex ? 'noindex,nofollow' : robots;
  const ogImage = image || DEFAULT_IMAGE;
  const ogImageAlt = `${siteName} preview image`;

  useEffect(() => {
    if (!d) return;

    // Title
    try { d.title = finalTitle; } catch {}

    // Primary meta
    upsertMetaByName(d, 'description', description);
    upsertMetaByName(d, 'keywords', keywords);
    upsertMetaByName(d, 'robots', robotsContent);
    upsertMetaByName(d, 'googlebot', robotsContent);
    upsertMetaByName(d, 'bingbot', robotsContent);
    upsertMetaByName(d, 'author', 'ChainPlay');
    upsertMetaByName(d, 'theme-color', THEME_COLOR);
    // Use dynamic active chain id (fallback to Base)
    const activeCid = resolveActiveChainId();
    upsertMetaByName(d, 'x-base-chain-id', activeCid);

    // Canonical + hreflang
    upsertLink(d, 'canonical', url);
    upsertLink(d, 'alternate', url, { hreflang: 'en' });
    // Hreflang x-default for canonical
    upsertLink(d, 'alternate', url, { hreflang: 'x-default' });

    // Favicons / PWA (expects files in /public)
    upsertLink(d, 'icon', '/chainplayfavconblue16.png', { type: 'image/png', sizes: '16x16' });
    upsertLink(d, 'icon', '/chainplayfavconblue32.png', { type: 'image/png', sizes: '32x32' });
    upsertLink(d, 'icon', '/bluedice192 copy.png', { type: 'image/png', sizes: '192x192' });
    upsertLink(d, 'icon', '/bluedice512.png', { type: 'image/png', sizes: '512x512' });
    upsertLink(d, 'apple-touch-icon', '/bluedice180.png', { sizes: '180x180' });
    upsertLink(d, 'manifest', '/manifest.json');
    upsertMetaByName(d, 'msapplication-TileColor', THEME_COLOR);
    upsertMetaByName(d, 'application-name', 'ChainPlay – The #1 Blockchain Casino');
    upsertMetaByName(d, 'apple-mobile-web-app-title', 'ChainPlay – The #1 Blockchain Casino');

    const rpc =
      (typeof process !== 'undefined' && process.env && (process.env.REACT_APP_PUBLIC_RPC_URL || process.env.VITE_PUBLIC_RPC_URL || process.env.VITE_RPC_URL)) ||
      'https://mainnet.base.org';
    const explorer = resolveExplorer(activeCid);
    const preconnectHosts = [ hostFrom(rpc), explorer || null, hostFrom(VIDEO_URL) ].filter(Boolean);
    preconnectHosts.forEach((h) => { upsertLink(d, 'preconnect', h, { crossOrigin: 'anonymous' }); upsertLink(d, 'dns-prefetch', h); });

    // Cleanup: remove any other favicon/apple-touch-icon tags so browsers don't pick stale ones
    try {
      const keep = new Set([
        '/chainplayfavconblue16.png',
        '/chainplayfavconblue32.png',
        '/bluedice192 copy.png',
        '/bluedice512.png',
        '/bluedice180.png',
      ]);
      d.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((el) => {
        const href = el.getAttribute('href') || '';
        if (!keep.has(href)) el.parentNode?.removeChild(el);
      });
    } catch {}

    // Open Graph
    upsertMetaByProp(d, 'og:type', type);
    upsertMetaByProp(d, 'og:site_name', siteName);
    upsertMetaByProp(d, 'og:locale', locale);
    upsertMetaByProp(d, 'og:url', url);
    upsertMetaByProp(d, 'og:title', finalTitle);
    upsertMetaByProp(d, 'og:description', description);
    upsertMetaByProp(d, 'og:image', ogImage);
    upsertMetaByProp(d, 'og:image:secure_url', ogImage);
    upsertMetaByProp(d, 'og:image:alt', ogImageAlt);
    const imgType = ogImage.endsWith('.jpg') || ogImage.endsWith('.jpeg') ? 'image/jpeg' : ogImage.endsWith('.webp') ? 'image/webp' : 'image/png';
    upsertMetaByProp(d, 'og:image:type', imgType);
    upsertMetaByProp(d, 'og:image:width', '1200');
    upsertMetaByProp(d, 'og:image:height', '630');
    upsertMetaByProp(d, 'og:updated_time', (modifiedTime || new Date().toISOString()));

    // Open Graph video (for rich previews where supported)
    upsertMetaByProp(d, 'og:video', VIDEO_URL);
    upsertMetaByProp(d, 'og:video:secure_url', VIDEO_URL);
    upsertMetaByProp(d, 'og:video:type', 'video/mp4');

    if (publishedTime) upsertMetaByProp(d, 'article:published_time', publishedTime);
    if (modifiedTime) upsertMetaByProp(d, 'article:modified_time', modifiedTime);

    // Twitter
    upsertMetaByName(d, 'twitter:card', 'summary_large_image');
    upsertMetaByName(d, 'twitter:site', DEFAULT_TWITTER);
    upsertMetaByName(d, 'twitter:creator', DEFAULT_TWITTER);
    upsertMetaByName(d, 'twitter:title', finalTitle);
    upsertMetaByName(d, 'twitter:description', description);
    upsertMetaByName(d, 'twitter:image', ogImage);
    upsertMetaByName(d, 'twitter:image:alt', finalTitle);

    // Twitter rich media (will fall back to summary_large_image when video not supported)
    upsertMetaByName(d, 'twitter:player', VIDEO_URL);
    upsertMetaByName(d, 'twitter:player:stream', VIDEO_URL);
    upsertMetaByName(d, 'twitter:player:stream:content_type', 'video/mp4');

    // Cleanup: ensure a single meta tag per name/property we manage
    try {
      const byName = [
        'description','keywords','robots','googlebot','bingbot','author','theme-color',
        'twitter:card','twitter:site','twitter:creator','twitter:title','twitter:description','twitter:image','twitter:image:alt',
      ];
      byName.forEach((name) => {
        const metas = d.querySelectorAll(`meta[name="${name}"]`);
        if (metas.length > 1) {
          // keep first, remove others
          metas.forEach((el, idx) => { if (idx > 0) el.parentNode?.removeChild(el); });
        }
      });
      const byProp = [
        'og:type','og:site_name','og:locale','og:url','og:title','og:description',
        'og:image','og:image:secure_url','og:image:alt','og:image:type','og:image:width','og:image:height','og:updated_time',
        'og:video','og:video:secure_url','og:video:type',
      ];
      byProp.forEach((prop) => {
        const metas = d.querySelectorAll(`meta[property="${prop}"]`);
        if (metas.length > 1) {
          metas.forEach((el, idx) => { if (idx > 0) el.parentNode?.removeChild(el); });
        }
      });
    } catch {}

    // JSON-LD
    upsertScriptJsonLd(d, 'ld-org', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteName,
      url: SITE_URL,
      logo: `${SITE_URL}/bluedice512.png`,
      sameAs: [
        'https://twitter.com/chainplaydotbet',
        'https://t.me/chainplay',
        'https://discord.gg/chainplay'
      ],
    });
    upsertScriptJsonLd(d, 'ld-website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteName,
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/search?q={query}`,
        'query-input': 'required name=query',
      },
    });

    // Breadcrumbs (Home > ...) for all routes
    try {
      const segs = safePath.split('/').filter(Boolean);
      const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL }];
      if (segs.length) {
        let acc = '';
        segs.forEach((s, i) => {
          acc += '/' + s;
          const name = s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          items.push({ '@type': 'ListItem', position: i + 2, name, item: `${SITE_URL}${acc}` });
        });
      }
      if (items.length > 1) {
        upsertScriptJsonLd(d, 'ld-breadcrumbs', {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: items,
        });
      }
    } catch {}

    // Game rich snippet when under /games/*
    try {
      if (safePath.startsWith('/games/')) {
        const seg = safePath.split('/')[2] || '';
        const gameName = seg ? seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Game';
        upsertScriptJsonLd(d, 'ld-game', {
          '@context': 'https://schema.org',
          '@type': 'VideoGame',
          name: `${gameName} – ${siteName}`,
          url,
          operatingSystem: 'Web',
          applicationCategory: 'Game',
          genre: ['Casino', 'Blockchain'],
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
          publisher: { '@type': 'Organization', name: siteName, url: SITE_URL },
          image: ogImage,
          description,
        });
      }
    } catch {}

    // Re-assert shortly after mount in case another effect runs later and overrides title/meta
    try {
      const t = setTimeout(() => {
        try { d.title = finalTitle; } catch {}
        upsertMetaByName(d, 'description', description);
        upsertMetaByName(d, 'twitter:title', finalTitle);
        upsertMetaByProp(d, 'og:title', finalTitle);
      }, 50);
      return () => clearTimeout(t);
    } catch {}
  }, [d, finalTitle, description, keywords, robotsContent, url, type, siteName, locale, ogImage, ogImageAlt, publishedTime, modifiedTime, safePath]);

  return null; // head-only component
}
